// UsersPage.jsx — system‑admin view of users within a school (AntD + Redux)
// -----------------------------------------------------------------------------
// Route: <Route exact path="/sysadmin/schools/:id" component={Auth(UsersPage, true, 3)} />
// • Pulls :id from URL → dispatch(getUsers(schoolId))
// • Global search across firstName, lastName, role, and _id
// • Uses Ant Design <Table>
// Lint‑safe: eliminates React‑hooks/exhaustive‑deps warnings by stabilising data refs
// -----------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from "react";
import { withRouter } from "react-router-dom";
import { useDispatch, connect } from "react-redux";
import { Table, Input, Row, Col, Button, Popconfirm, message } from "antd";
import { getUsers, impersonateUser, clearMessage, pauseSchool, isPaused } from "../../../_actions/user_actions";
import "./Users.css";
import DeleteSchoolButton from "./DeleteSchoolButton";

function UsersPage(props) {
  const dispatch   = useDispatch();
  const { id: schoolId } = props.match.params;            // /sysadmin/schools/:id
  const usersData  = props.data.user?.usersData;          // may be undefined until fetched
  const [search, setSearch] = useState("");

  // ─── Fetch users on mount / when schoolId changes ───────────────────────────
  useEffect(() => {
    if (schoolId) dispatch(getUsers({_id: schoolId}));
    if (schoolId) dispatch(isPaused({_id: schoolId}));
  }, [dispatch, schoolId]);

  useEffect(() => {
    if (!props.user.message) return;
    if (props.user.message === "School Deleted") {
      message.success(props.user.message)
      props.history.push("/sysadmin/schools")
    }
    else if (props.user.message === "Pause Toggled") {
      message.success(props.user.message)
    }
    else {
        message.error(props.user.message)
    }
    dispatch(clearMessage())
  }, [props.user.message, dispatch, props.history])

  // ─── Derived + memoised: filteredUsers ──────────────────────────────────────
  const filteredUsers = useMemo(() => {
    const list = Array.isArray(usersData) ? usersData : [];                        // stable reference each render
    const filter = search.trim().toLowerCase();
    if (!filter) return list;

    return list.filter((u) => {
      const fn = (u.firstName || "").toLowerCase();
      const ln = (u.lastName  || "").toLowerCase();
      const rl = (String(u.role)      || "").toLowerCase();
      const id = (u._id       || "").toLowerCase();
      return fn.includes(filter) || ln.includes(filter) || rl.includes(filter) || id.includes(filter);
    });
  }, [usersData, search]);

  // ─── AntD Table columns ─────────────────────────────────────────────────────
  const columns = [
    { title: "First Name",      dataIndex: "firstName",    key: "firstName",    sorter: (a, b) => a.firstName.localeCompare(b.firstName) },
    { title: "Last Name",       dataIndex: "lastName",     key: "lastName",     sorter: (a, b) => a.lastName.localeCompare(b.lastName) },
    { title: "Role",            dataIndex: "role",         key: "role",         sorter: (a, b) => String(a.role).localeCompare(String(b.role)) },
    { title: "ID",              dataIndex: "_id",          key: "_id",          ellipsis: true },
    {
      title: "Rank",
      dataIndex: "rank",
      key: "rank",
      align: "center",
      sorter: (a, b) => (a.rank ?? 0) - (b.rank ?? 0),
      render: (v) => (v ?? 0).toLocaleString(),
    },
    {
      title: "Net Worth",
      dataIndex: "netWorth",
      key: "netWorth",
      align: "center",
      sorter: (a, b) => (a.netWorth ?? 0) - (b.netWorth ?? 0),
      render: (v) => (v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 }),
    },
    {
      title: "Taxable Income",
      dataIndex: "taxableIncome",
      key: "taxableIncome",
      align: "center",
      sorter: (a, b) => (a.taxableIncome ?? 0) - (b.taxableIncome ?? 0),
      render: (v) => (v ?? 0).toLocaleString(),
    },
    {
      title: "Income",
      dataIndex: "income",
      key: "income",
      align: "center",
      sorter: (a, b) => (a.income ?? 0) - (b.income ?? 0),
      render: (v) => (v ?? 0).toLocaleString(),
    },
    { title: "College",         dataIndex: "college",      key: "college",      align: "center", render: (val) => (val ? "Yes" : "No") },
    {
      title: "Lotto Play",
      dataIndex: "lottoPlay",
      key: "lottoPlay",
      align: "center",
      sorter: (a, b) => (a.lottoPlay ?? 0) - (b.lottoPlay ?? 0),
      render: (v) => (v ?? 0).toLocaleString(),
    },
    {
      title: "Lotto Win",
      dataIndex: "lottoWin",
      key: "lottoWin",
      align: "center",
      sorter: (a, b) => (a.lottoWin ?? 0) - (b.lottoWin ?? 0),
      render: (v) => (v ?? 0).toLocaleString(),
    },
    {
      title: "", // no header, keeps table compact
      key: "actions",
      align: "center",
      render: (_, record) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Popconfirm
            title={`Impersonate ${record._id}?`}
            okText="Yes, continue"
            cancelText="No"
            onConfirm={(e) => {
              e.stopPropagation();
              dispatch(impersonateUser(record._id));
            }}
            onCancel={(e) => e.stopPropagation()}
          >
            <Button
              size="small"
              type="primary"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              Impersonate
            </Button>
          </Popconfirm>
        </div>
      ),

    },
  ];

  const onRowClick = (record) => ({
  onClick: (e) => {
    if (e.target.closest("button") || e.target.closest(".ant-popover")) {
      return; // ignore clicks on buttons/popups, no row navigation!
    }
    props.history.push(`/sysadmin/schools/${schoolId}/user/${record._id}`);
  },
});

  // ─── Toolbar: search + profile button ───────────────────────────────────────
  const Toolbar = () => (
    <div className="innerBody">
      <Row gutter={16} className="admin‑toolbar">
        <Col span={8}>
          <Input
            autoFocus
            placeholder="Search users"
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>

        <Col span={4} className="text‑center">
          <Button
            type="primary"
            onClick={() => dispatch(pauseSchool({_id: schoolId}))}
          >
            {props.data.user.isPaused ? "Unpause School" : "Pause School"}
          </Button>
        </Col>

        <Col className="text‑center">
          <DeleteSchoolButton schoolId={schoolId} />
        </Col>
      </Row>
    </div>
  );

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="spread">
      <Toolbar />
      <br />
      <Table
        rowKey="_id"
        dataSource={filteredUsers}
        columns={columns}
        onRow={onRowClick}
        pagination={{ pageSize: 20 }}
        className="admin-table"
      />
    </div>
  );
}

function mapStateToProps(state) {
  return { data: state };
}

export default withRouter(connect(mapStateToProps)(UsersPage));
